/* eslint-disable react-hooks/refs -- dispatchTransfer writes amountDepositPermitRef, but it only runs on click; the rule sees the reference during the onClick render. */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
/* Components */
import { Modal, Form, InputGroup } from "react-bootstrap";
import WrongNetworkAlert from "./WrongNetworkAlert";
import TxButton from "../CommonForBoth/TxButton";
/* Helpers */
import { strToDec, strToDecZ, strToBN, BNToDecimal } from "@/helpers/number_format_helper";
import { isTransactionValid } from "@/helpers/store_helper";
import { calcOnLoad } from "@/helpers/ensuro_helper";
import { useModalTxError } from "@/hooks/useModalTxError";
/* Stores */
import { selectEthCallMultiple, selectLastTransact, selectBiggerSign } from "@ensuro/ethereum-store";
import { selectAPICallMultiple } from "@ensuro/api-calls-store";
import { selectCurrent } from "@store/eTokens/selectors";
import { selectChainId } from "@/store/user/selectors";
/* i18n and Config */
import { connect, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { chain, currency, kycApi } from "@/config";
import { dispatchPermitSign } from "@/helpers/depositPermit_helper";
import { dispatchAPICall } from "@/helpers/api_store_helper";
import "./style.scss";

import { ethers } from "ethers";
import { getContract } from "@/contractRegistry";

const makeEthCalls = function (userAddress, poolAddress, etkAddress) {
  return [
    { address: currency.address, abi: "ERC20Permit", method: "nonces", args: [userAddress] },
    { address: currency.address, abi: "ERC20Permit", method: "name", args: [] },
    { address: currency.address, abi: "ERC20", method: "allowance", args: [userAddress, poolAddress] },
    { address: currency.address, abi: "ERC20", method: "balanceOf", args: [userAddress] },
    { address: etkAddress, abi: "EToken", method: "minUtilizationRate", args: [] },
    { address: etkAddress, abi: "EToken", method: "totalSupply", args: [] },
    { address: etkAddress, abi: "EToken", method: "scr", args: [] },
  ];
};

const makeDepositCheckCall = () => [{ apiName: "depositCheck", args: [] }];

const getTxHash = (tx) => tx?.hash || tx?.txHash || tx?.transactionHash;
const getTxMethod = (tx) => tx?.method;
const getTxStatus = (tx) => tx?.state || tx?.status;

const isConfirmedTx = (tx) => {
  if (!tx) return false;
  if (tx.receipt) return true;
  const status = getTxStatus(tx);
  return status === "MINED";
};

const DepositModal = ({
  t,
  lastTransaction,
  show,
  onHide,
  balance,
  user,
  userAddress,
  allowance,
  nonces,
  currencyName,
  permit,
  minUtilizationRate,
  totalSupply,
  scr,
  poolAddress,
  etkAddress,
  chainId,
  openedAfterApprove = false,
  depositCheck = {},
}) => {
  const dispatch = useDispatch();
  const { errorToShow, clearError, wrapDispatch } = useModalTxError(lastTransaction);
  const [userAmount, setUserAmount] = useState("");
  const [infititeApproval, setInfititeApproval] = useState(false);
  const [usePermit, setUsePermit] = useState(true);
  const [reSignTrigger, setReSignTrigger] = useState(0);
  const [pendingCheck, setPendingCheck] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const usePermitFlow = usePermit;
  const gateEnabled = Boolean(kycApi.url);
  const ready =
    allowance.state === "LOADED" &&
    balance.state === "LOADED" &&
    (!usePermitFlow || nonces.state === "LOADED") &&
    chainId === chain.id;
  const isOversold = calcOnLoad((ts, scr, minUR) => scr / ts < minUR, totalSupply, scr, minUtilizationRate);
  const lastRefreshedHash = useRef(null);
  const amountDepositPermitRef = useRef(null);
  const proceedRef = useRef(null);

  const handleHide = () => {
    amountDepositPermitRef.current = null;
    setPendingCheck(null);
    setCheckError(null);
    clearError();
    onHide();
  };

  useEffect(() => {
    if (userAddress && etkAddress && poolAddress !== null) {
      dispatch({
        type: "ETH_ADD_SUBSCRIPTION",
        key: "depositModal",
        componentEthCalls: makeEthCalls(userAddress, poolAddress, etkAddress),
      });
    }
    return () => {
      dispatch({ type: "ETH_REMOVE_SUBSCRIPTION", key: "depositModal" });
    };
  }, [dispatch, etkAddress, poolAddress, userAddress]);
  useEffect(() => {
    if (!userAddress || !etkAddress || !poolAddress) return;
    if (!isConfirmedTx(lastTransaction)) return;

    const hash = getTxHash(lastTransaction);
    const method = getTxMethod(lastTransaction);

    if (!hash) return;
    if (lastRefreshedHash.current === hash) return;
    if (method !== "approve" && method !== "deposit" && method !== "depositWithPermit") return;

    lastRefreshedHash.current = hash;

    dispatch({ type: "ETH_REMOVE_SUBSCRIPTION", key: "depositModal" });
    dispatch({
      type: "ETH_ADD_SUBSCRIPTION",
      key: "depositModal",
      componentEthCalls: makeEthCalls(userAddress, poolAddress, etkAddress),
    });

    if (method === "deposit" || method === "depositWithPermit") setUserAmount("");
  }, [dispatch, lastTransaction, userAddress, poolAddress, etkAddress]);

  useEffect(() => {
    const amountBN = amountDepositPermitRef.current;
    if (
      amountBN == null ||
      permit?.state !== "SIGNED" ||
      !permit?.value ||
      !permit?.signature ||
      BigInt(permit.value.value) < amountBN
    )
      return;
    if (!poolAddress || !etkAddress || !userAddress) return;
    const sig = ethers.Signature.from(permit.signature);
    dispatch({
      type: "ETH_TRANSACT",
      address: poolAddress,
      abi: "PolicyPool",
      method: "depositWithPermit",
      args: [etkAddress, amountBN, userAddress, permit.value.deadline, sig.v, sig.r, sig.s],
    });
    amountDepositPermitRef.current = null;
  }, [permit?.state, permit?.value, permit?.signature, reSignTrigger, dispatch, poolAddress, etkAddress, userAddress]);

  const invalidBalance = () => {
    return !balance.value || (strToDec(userAmount) && strToDec(userAmount).gt(balance.value));
  };

  const requiresApprove = (amountStr) => {
    const ua = strToDec(amountStr);
    if (ua === undefined) return false;
    if (usePermitFlow && permit?.state === "SIGNED" && permit?.value?.value != null) {
      if (BNToDecimal(BigInt(permit.value.value), currency.decimals).gte(ua)) return false;
    }
    if (allowance.value && allowance.value.gte(ua)) return false;
    return true;
  };
  const showCheckingAllowance = openedAfterApprove && requiresApprove(userAmount);
  const hasValidPermitForAmount = (amountStr) => {
    const amountBN = strToBN(amountStr);
    return (
      usePermitFlow &&
      permit?.state === "SIGNED" &&
      permit?.value &&
      permit?.signature &&
      amountBN != null &&
      BigInt(permit.value.value) >= amountBN
    );
  };

  const proceedWithDeposit = (amountBN, amountStr) => {
    if (requiresApprove(amountStr)) {
      if (usePermitFlow) {
        amountDepositPermitRef.current = amountBN;
        dispatchPermitSign(dispatch, {
          userAddress,
          spenderAddress: poolAddress,
          amount: amountBN,
          nonce: nonces?.value,
          chainId,
          currency: {
            name: currencyName.value,
            version: currency.version,
            address: currency.address,
          },
        });
      } else {
        const amount = infititeApproval ? ethers.MaxInt256 : amountBN;
        dispatch({
          type: "ETH_TRANSACT",
          address: currency.address,
          abi: "ERC20",
          method: "approve",
          args: [poolAddress, amount],
        });
      }
    } else if (hasValidPermitForAmount(amountStr)) {
      const sig = ethers.Signature.from(permit.signature);
      const args = [etkAddress, amountBN, userAddress, permit.value.deadline, sig.v, sig.r, sig.s];

      const executeDepositWithPermit = async () => {
        let isPermitValid = false;
        try {
          const tokenContract = getContract(currency.address, "ERC20Permit");

          const permitArgs = [userAddress, poolAddress, amountBN, permit.value.deadline, sig.v, sig.r, sig.s];

          await tokenContract.permit.staticCall(...permitArgs);

          isPermitValid = true;
        } catch {
          amountDepositPermitRef.current = amountBN;
          setReSignTrigger((prev) => prev + 1);
          dispatchPermitSign(dispatch, {
            userAddress,
            spenderAddress: poolAddress,
            amount: amountBN,
            nonce: nonces?.value,
            chainId,
            currency: {
              name: currencyName.value,
              version: currency.version,
              address: currency.address,
            },
          });
        }

        if (isPermitValid) {
          dispatch({
            type: "ETH_TRANSACT",
            address: poolAddress,
            abi: "PolicyPool",
            method: "depositWithPermit",
            args,
          });
        }
      };

      executeDepositWithPermit();
    } else {
      dispatch({
        type: "ETH_TRANSACT",
        address: poolAddress,
        abi: "PolicyPool",
        method: "deposit",
        args: [etkAddress, amountBN, userAddress],
      });
    }
  };

  const dispatchTransfer = () => {
    if (!isTransactionValid(lastTransaction)) return;
    const amountDec = strToDec(userAmount);
    const amountBN = strToBN(userAmount);
    if (amountDec === undefined || amountBN == null) return;
    setCheckError(null);
    if (!gateEnabled) {
      proceedWithDeposit(amountBN, userAmount);
      return;
    }
    setPendingCheck({ amountBN, amountStr: userAmount, entryBefore: depositCheck });
    dispatchAPICall(dispatch, {
      apiName: "depositCheck",
      args: [],
      method: "POST",
      data: {
        address: ethers.getAddress(userAddress),
        amount: amountDec.toFixed(currency.decimals, 0),
      },
      forceCall: true,
    });
  };

  useEffect(() => {
    proceedRef.current = proceedWithDeposit;
  });

  useEffect(() => {
    if (!pendingCheck) return;
    const entry = depositCheck || {};
    if (entry === pendingCheck.entryBefore) return;
    if (entry.state === "LOADING" || entry.state === undefined) return;

    setPendingCheck(null);

    if (entry.state === "LOADED") {
      if (entry.value?.allowed) {
        proceedRef.current(pendingCheck.amountBN, pendingCheck.amountStr);
        return;
      }
      setCheckError(
        t("This deposit exceeds your investment limit. Please contact our compliance team at compliance@ensuro.co.")
      );
      return;
    }

    if (entry.state === "ERROR" && entry.code === 429) {
      setCheckError(t("Too many attempts. Please wait a minute and try again."));
      return;
    }

    setCheckError(t("We could not verify your investment limit. Please try again."));
  }, [depositCheck, pendingCheck, t]);

  const buttonText = () => {
    if (requiresApprove(userAmount) && !usePermitFlow) return t("Approve");
    return t("Deposit ", { currency });
  };

  return (
    <Modal show={show} centered onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>{t("Deposit ", { currency })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {chainId !== chain.id && <WrongNetworkAlert />}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t("Amount")}</Form.Label>
            <InputGroup>
              <Form.Control
                placeholder="0.00"
                id="amount"
                isInvalid={userAmount !== "" && (!strToDecZ(userAmount) || invalidBalance() || isOversold?.value)}
                value={userAmount}
                onChange={(e) => {
                  setPendingCheck(null);
                  setCheckError(null);
                  setUserAmount(e.target.value || "");
                }}
              />
              <InputGroup.Text>{currency.name}</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {!strToDecZ(userAmount) ? (
                  t("Please provide a valid amount.")
                ) : invalidBalance() ? (
                  <span>{t("Your balance is not enough.")}</span>
                ) : (
                  isOversold?.value && <span>{t("Utilization Rate is under Min Utilization Rate.")}</span>
                )}
              </Form.Control.Feedback>
            </InputGroup>
            <p className="align-end">
              {t("YourCurrencyBalance", { currency })}: {balance.value != null ? balance.value * 1 : "—"}
              <strong
                onClick={() => setUserAmount(balance.value != null ? balance.value.toString() : "")}
                className="btn-text"
              >
                {t("MAX")}
              </strong>
            </p>
          </Form.Group>

          {requiresApprove(userAmount) && (
            <>
              {showCheckingAllowance && <p>{t("Checking allowance...")}</p>}
              {!showCheckingAllowance && !usePermitFlow && <p>{t("Please approve before depositing")}</p>}
              {!showCheckingAllowance && !usePermitFlow && (
                <Form.Check
                  type="switch"
                  id="custom-switch"
                  checked={infititeApproval}
                  onChange={() => setInfititeApproval(!infititeApproval)}
                  label={t("Infinite Approval")}
                />
              )}
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-block">
        <em style={{ color: "red", display: "block" }}>{checkError ?? errorToShow ?? ""}</em>
        <div className="d-flex align-items-center justify-content-between gap-2" style={{ marginTop: "0.5rem" }}>
          {requiresApprove(userAmount) && (
            <Form.Check
              type="switch"
              id="deposit-use-permit"
              checked={usePermit}
              onChange={() => setUsePermit(!usePermit)}
              label={t("Use permit")}
              className="mb-0"
            />
          )}
          <div style={{ marginLeft: "auto" }}>
            <TxButton
              chainId={chainId}
              disabled={
                pendingCheck != null ||
                showCheckingAllowance ||
                !ready ||
                !strToDecZ(userAmount) ||
                invalidBalance() ||
                (user && !user.canTransact) ||
                isOversold?.value
              }
              onClick={wrapDispatch(dispatchTransfer)}
              text={
                pendingCheck != null
                  ? t("Verifying...")
                  : showCheckingAllowance
                    ? t("Checking allowance...")
                    : buttonText()
              }
            />
          </div>
        </div>
      </Modal.Footer>
      {user && !user.canTransact && <p className="read-only">{t("ReadOnlyModeText")}</p>}
    </Modal>
  );
};

DepositModal.propTypes = {
  t: PropTypes.any,
};

const mapStateToProps = (state) => {
  const user = state.UserReducer;
  const userAddress = user.address;
  const etk = selectCurrent(state.ETokensReducer);
  const etkAddress = etk.address;
  const etkSymbol = etk.symbol;
  const url = new URL(etk.pool);
  const poolAddress = url.pathname.split("/")[3];
  const lastTransaction = selectLastTransact(state.EthereumReducer);
  const chainId = selectChainId(state.UserReducer);

  const callsResult = !userAddress
    ? null
    : selectEthCallMultiple(state.EthereumReducer, makeEthCalls(userAddress, poolAddress, etkAddress));

  const [nonces, currencyName, allowance, balance, minUtilizationRate, totalSupply, scr] =
    !userAddress || !callsResult ? [{}, {}, {}, {}, {}, {}, {}] : callsResult;

  const permit =
    userAddress && poolAddress
      ? selectBiggerSign(state.EthereumReducer, userAddress, nonces?.value, poolAddress)
      : null;

  const [depositCheck] = userAddress ? selectAPICallMultiple(state.APIReducer, makeDepositCheckCall()) : [{}];

  return {
    lastTransaction,
    user,
    userAddress,
    allowance,
    balance,
    nonces,
    currencyName,
    permit,
    minUtilizationRate,
    totalSupply,
    scr,
    poolAddress,
    etkAddress,
    etkSymbol,
    chainId,
    depositCheck,
  };
};

export default connect(mapStateToProps)(withTranslation()(DepositModal));
